import React, { useMemo, useState } from 'react';
import {
  Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, TextInput, panelGet, panelPost, useAdminGet,
} from '../../kit';
import { AdminLink, openAdmin } from '../../adminNav';
import { Check } from '../../email/emailKit';
import { emailPeople } from '../../email/people/shared';
import ProspectDrawer from './ProspectDrawer';
import ScoringEditor from './ScoringEditor';
import {
  LEADS, ProspectRow, SEGMENT_OPTIONS, SIGNAL_LABELS, ScorePill, SegmentPill, WINDOWS, ago, fmtSignal,
} from './shared';

/**
 * Leads & Marketing → Prospects (owner + Parag, 2026-10-18): every account
 * that does not pay — Explorers, trials running, trials lapsed — ranked by a
 * 0-100 score of intent and activity, with the reasons, a suggested nudge
 * and, for trial-lapsed people, how active they were relative to the others.
 * Backend: admin_prospects.py. Nothing here sends: "Follow up selected" hands
 * the people (and the nudge's template) to Email Center → Campaigns, where
 * the admin reviews and sends. Automatic follow-up is MEMAC's PR-0x triggers.
 */

const VIEWS = ['Ranked list', 'Scoring'];
type Flag = '' | 'checkout_abandoned' | 'trial_lapsed_active' | 'paywall_hitters';
type Flash = { kind: 'error' | 'ok'; text: string } | null;

/** Signal columns that can be switched on from the table's Columns picker. */
const SIGNAL_COLUMNS = ['searches_w', 'searches', 'active_days_w', 'paywall_w', 'upgrade_clicks_w',
  'trial_intent_w', 'checkout_abandoned_n', 'contact_blocked_w', 'limit_requests', 'concierge', 'ip_intel',
  'saved', 'logins', 'email_clicks_w', 'profile_pct'];

const ProspectsTab: React.FC = () => {
  const [view, setView] = useState(VIEWS[0]);
  const [segment, setSegment] = useState('all');
  const [band, setBand] = useState('all');
  const [windowDays, setWindowDays] = useState('30');
  const [q, setQ] = useState('');
  const [minScore, setMinScore] = useState('0');
  const [flag, setFlag] = useState<Flag>('');
  const [includeUnsigned, setIncludeUnsigned] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, string> = { segment, window: windowDays, limit: '1000', sort: 'score' };
    if (band !== 'all') p.band = band;
    if (flag) p.flag = flag;
    if (q.trim()) p.q = q.trim();
    if (Number(minScore) > 0) p.min_score = String(Math.min(100, Math.max(0, Number(minScore) || 0)));
    if (includeUnsigned) p.include_unsigned = '1';
    return p;
  }, [segment, band, windowDays, q, minScore, flag, includeUnsigned]);
  const data = useAdminGet(LEADS, 'prospects', params,
    [segment, band, windowDays, q, minScore, flag, includeUnsigned]);
  // Refresh: one read that bypasses the server's 90-second cache, then the normal read.
  const refresh = async () => {
    await panelGet(LEADS, 'prospects', { ...params, refresh: '1', limit: '1' });
    data.reload();
  };
  const d: any = data.data || {};
  const k: any = d.kpis || {};
  const rows: ProspectRow[] = d.rows || [];

  const toggleBand = (b: string) => { setFlag(''); setBand(band === b ? 'all' : b); };
  const toggleFlag = (f: Flag) => { setBand('all'); setFlag(flag === f ? '' : f); };
  const tick = (id: string, on: boolean) => setSelected(s => {
    const n = new Set(s);
    if (on) n.add(id); else n.delete(id);
    return n;
  });

  const followUp = async () => {
    const ids = rows.filter(r => selected.has(r.user_id)).map(r => r.user_id);
    if (!ids.length) return;
    // One template for the batch: the most common suggestion among the picked people.
    const counts: Record<string, number> = {};
    rows.filter(r => selected.has(r.user_id)).forEach(r => { counts[r.suggestion.key] = (counts[r.suggestion.key] || 0) + 1; });
    const key = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
    setBusy(true);
    const { data: res, error } = await panelPost(LEADS, 'prospects_followup', { user_ids: ids, suggestion_key: key });
    setBusy(false);
    if (error || !res) { setFlash({ kind: 'error', text: error || 'Could not prepare the follow-up' }); return; }
    emailPeople(res.user_ids || ids, { template: res.template_key || null, source: 'Leads → Prospects' });
    openAdmin(res.deep_link || { group: 'email', tab: 'campaigns' });
  };

  const tableRows = rows.map(r => ({
    ...r,
    select: '',
    person: r.name || r.email,
    score_pct: r.score,
    top_reasons: r.reasons.slice(0, 3).join(' · '),
    nudge: r.suggestion.label,
    percentile: r.activity_percentile,
    ...Object.fromEntries(SIGNAL_COLUMNS.map(c => [c, fmtSignal(r.signals[c])])),
  }));
  const allShownPicked = rows.length > 0 && rows.every(r => selected.has(r.user_id));

  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={VIEWS} active={view} onChange={setView} />
      {view === 'Scoring' && <ScoringEditor onSaved={refresh} />}
      {view === 'Ranked list' && (
        <>
          <Section title="Prospects: interested, not paying yet"
            right={<Btn small onClick={refresh}>Refresh</Btn>}>
            <div className="flex gap-1 flex-wrap items-center">
              {SEGMENT_OPTIONS.map(o => (
                <button key={o.key} type="button" onClick={() => setSegment(o.key)}
                  className={`py-1 px-3 rounded-full text-xs font-grotesk ${segment === o.key
                    ? 'bg-primary-500 text-grayscale-100 font-bold'
                    : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                  {o.label}{k.by_segment && o.key !== 'all' ? ` (${k.by_segment[o.key] ?? 0})` : ''}
                </button>
              ))}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <Select value={band} onChange={v => { setFlag(''); setBand(v); }} options={['all', 'hot', 'warm', 'cool']}
                labels={{ all: 'Any band', hot: 'Hot', warm: 'Warm', cool: 'Cool' }} />
              <Select value={windowDays} onChange={setWindowDays} options={WINDOWS}
                labels={Object.fromEntries(WINDOWS.map(w => [w, `Last ${w} days`]))} />
              <label className="text-xs text-grayscale-1100 flex items-center gap-1">Min score
                <input type="number" min={0} max={100} value={minScore} onChange={e => setMinScore(e.target.value)}
                  className="h-9 w-16 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
              </label>
              <TextInput value={q} onChange={setQ} placeholder="Name, e-mail or organisation…" wide />
              <Check checked={includeUnsigned} onChange={setIncludeUnsigned}>Include never signed in</Check>
            </div>
            <p className="text-xs text-grayscale-1100">
              Score = intent first (abandoned checkout, upgrade and trial clicks, blocked contacts, paywalls),
              then recent activity, searches, saves, e-mail clicks and profile — weights in Scoring.
              The team, suspended accounts and paying / assigned Voyagers are left out.
              For automatic follow-up, switch on the PR-01 / PR-02 / PR-03 triggers in{' '}
              <AdminLink to={{ group: 'email', tab: 'memac', view: 'triggers' }}>Email Center → MEMAC</AdminLink>{' '}
              (seeded off; they also need MEMAC's global switch and MEMAC_AUTO=1 on the server).
            </p>
          </Section>

          {data.error && <Msg kind="error">{data.error}</Msg>}
          {flash && <Msg kind={flash.kind}>{flash.text}</Msg>}
          {(d.missing_sources || []).length > 0 && (
            <Msg kind="error">Not counted (table missing or unreadable): {(d.missing_sources || []).join(', ')}.</Msg>
          )}

          {data.data && (
            <KPIRow>
              <KPI label="Hot" value={k.hot ?? 0} hint={`score ≥ ${d.settings?.bands?.hot ?? 60}`}
                onClick={() => toggleBand('hot')} active={band === 'hot'} />
              <KPI label="Warm" value={k.warm ?? 0} hint={`score ≥ ${d.settings?.bands?.warm ?? 35}`}
                onClick={() => toggleBand('warm')} active={band === 'warm'} />
              <KPI label="Cool" value={k.cool ?? 0} onClick={() => toggleBand('cool')} active={band === 'cool'} />
              <KPI label="Abandoned checkout" value={k.checkout_abandoned ?? 0} hint="opened, did not pay"
                onClick={() => toggleFlag('checkout_abandoned')} active={flag === 'checkout_abandoned'} />
              <KPI label="Trial-lapsed active" value={k.trial_lapsed_active ?? 0}
                hint={`activity ≥ ${d.settings?.thresholds?.active_percentile ?? 60}th pct`}
                onClick={() => toggleFlag('trial_lapsed_active')} active={flag === 'trial_lapsed_active'} />
              <KPI label="Paywall hitters" value={k.paywall_hitters ?? 0} hint={`in ${windowDays} days`}
                onClick={() => toggleFlag('paywall_hitters')} active={flag === 'paywall_hitters'} />
            </KPIRow>
          )}

          <Section title={`Ranked: ${d.matched ?? 0} of ${k.total ?? 0}${d.matched > rows.length ? ` (top ${rows.length} shown)` : ''}`}
            right={
              <div className="flex gap-2 items-center">
                <Btn small onClick={() => setSelected(allShownPicked ? new Set() : new Set(rows.map(r => r.user_id)))}>
                  {allShownPicked ? 'Clear selection' : 'Select all shown'}
                </Btn>
                <Btn small kind="primary" disabled={!selected.size || busy} onClick={followUp}
                  title="Opens Email Center → Campaigns with these people picked. Nothing is sent from here.">
                  {busy ? 'Preparing…' : `Follow up selected (${selected.size}) → Campaigns`}
                </Btn>
              </div>}>
            {data.loading ? <Loading label="Scoring every non-paying account…" /> : (
              <DataTable rows={tableRows} csvName="prospects" tableId="leads_prospects"
                columns={['select', 'rank', 'person', 'segment_label', 'score_pct', 'percentile', 'top_reasons',
                  'nudge', 'last_active_at']}
                optionalColumns={['email', 'organisation', 'trial_started_at', 'trial_ended_at', 'trial_origin',
                  ...SIGNAL_COLUMNS]}
                labels={{ select: '', rank: '#', person: 'Name', segment_label: 'Segment', score_pct: 'Score',
                  percentile: 'Trial-lapsed activity pct', top_reasons: 'Why', nudge: 'Suggested nudge',
                  last_active_at: 'Last active', ...SIGNAL_LABELS }}
                unsortable={['select']}
                empty="Nobody matches these filters."
                render={{
                  select: r => (
                    <input type="checkbox" checked={selected.has(r.user_id)}
                      onChange={e => tick(r.user_id, e.target.checked)} aria-label={`Select ${r.email}`} />
                  ),
                  person: r => (
                    <div className="flex flex-col">
                      <AdminLink to={{ group: 'user360', user: r.user_id }} title="Open in User 360">
                        {r.name || r.email}
                      </AdminLink>
                      {r.name && <span className="text-[11px] text-grayscale-1100">{r.email}</span>}
                    </div>
                  ),
                  segment_label: r => <SegmentPill segment={r.segment} label={r.segment_label} />,
                  score_pct: r => <ScorePill score={r.score} band={r.band} />,
                  percentile: r => (r.activity_percentile === null || r.activity_percentile === undefined ? '—'
                    : <span title="Activity since the trial started, ranked among everyone trial-lapsed">
                        {r.activity_percentile}%</span>),
                  top_reasons: r => <span className="text-xs">{r.top_reasons || '—'}</span>,
                  nudge: r => <span className="text-xs" title={r.suggestion.why}>{r.suggestion.label}</span>,
                  last_active_at: r => <span className="text-xs whitespace-nowrap">{ago(r.last_active_at) || '—'}</span>,
                }}
                actions={r => <Btn small onClick={() => setOpen(r.user_id)}>Details</Btn>} />
            )}
          </Section>
          {open && <ProspectDrawer userId={open} windowDays={windowDays} onClose={() => setOpen(null)}
            onFollowUp={(id, tpl) => {
              emailPeople([id], { template: tpl, source: 'Leads → Prospects' });
              openAdmin({ group: 'email', tab: 'campaigns' });
            }} />}
        </>
      )}
    </div>
  );
};

export default ProspectsTab;
